import { useMemo } from "react";
import { Row, Col } from "reactstrap";
import { TreeSelect } from 'primereact/treeselect';
import { Enum } from "helpers";

// ── Pure utility functions (outside component) ────────────────────

/**
 * Maps hierarchy data to PrimeReact TreeSelect options format.
 */
const mapHierarchyToOptions = (hierarchy) =>
  hierarchy.map(category => ({
    key: category.category.categoryId,
    label: category.category.name,
    children: category.subCategories.length > 0
      ? category.subCategories.map(sub => ({
          key: sub.subCategory.subCategoryId,
          label: sub.subCategory.name,
          children: sub.skills.map(skill => ({
            key: skill.skillId,
            label: skill.name,
          })),
        }))
      : category.skills.map(skill => ({
          key: skill.skillId,
          label: skill.name,
        })),
  }));

/**
 * Builds name lookup maps for categories, subcategories, and skills.
 */
const buildNameMaps = (hierarchy) => {
  const skillMap = {};
  const subCatMap = {};
  const catMap = {};

  hierarchy.forEach(category => {
    catMap[category.category.categoryId] = category.category.name;
    if (category.subCategories.length > 0) {
      category.subCategories.forEach(sub => {
        subCatMap[sub.subCategory.subCategoryId] = sub.subCategory.name;
        sub.skills.forEach(skill => { skillMap[skill.skillId] = skill.name; });
      });
    } else {
      category.skills.forEach(skill => { skillMap[skill.skillId] = skill.name; });
    }
  });

  return { skillMap, subCatMap, catMap };
};

/**
 * Builds relationship maps for enforcing hierarchical selection rules:
 *   ancestors[nodeKey]   = [parentKey, grandparentKey, ...]  (for auto-select UP)
 *   descendants[nodeKey] = [childKey, grandchildKey, ...]    (for auto-deselect DOWN)
 */
const buildRelationMaps = (hierarchy) => {
  const ancestors = {};
  const descendants = {};

  hierarchy.forEach(category => {
    const catId = category.category.categoryId;
    ancestors[catId] = [];
    descendants[catId] = [];

    if (category.subCategories.length > 0) {
      category.subCategories.forEach(sub => {
        const subId = sub.subCategory.subCategoryId;
        ancestors[subId] = [catId];
        descendants[subId] = [];
        descendants[catId].push(subId);

        sub.skills.forEach(skill => {
          ancestors[skill.skillId] = [subId, catId];
          descendants[skill.skillId] = [];
          descendants[subId].push(skill.skillId);
          descendants[catId].push(skill.skillId);
        });
      });
    } else {
      category.skills.forEach(skill => {
        ancestors[skill.skillId] = [catId];
        descendants[skill.skillId] = [];
        descendants[catId].push(skill.skillId);
      });
    }
  });

  return { ancestors, descendants };
};

/**
 * Converts our clean keys object into the app's selectedSkills[] format.
 * Each checked level is recorded independently.
 */
const processKeysToSkills = (hierarchy, keys) => {
  const selectedSkills = [];
  const selectedSkillsNames = [];

  hierarchy.forEach(category => {
    const { categoryId, name: categoryName } = category.category;

    if (category.subCategories.length > 0) {
      // Only emit category if none of its sub-categories or their skills are selected
      const hasSelectedDescendants = category.subCategories.some(sub =>
        keys[sub.subCategory.subCategoryId]?.checked ||
        sub.skills.some(skill => keys[skill.skillId]?.checked)
      );

      if (keys[categoryId]?.checked && !hasSelectedDescendants) {
        selectedSkills.push({ categoryId, subcategoryId: null, skillId: null });
        selectedSkillsNames.push(categoryName);
      }

      category.subCategories.forEach(sub => {
        const { subCategoryId, name: subCategoryName } = sub.subCategory;

        // Only emit sub-category if none of its skills are selected
        const hasSelectedSkills = sub.skills.some(skill => keys[skill.skillId]?.checked);

        if (keys[subCategoryId]?.checked && !hasSelectedSkills) {
          selectedSkills.push({ categoryId, subcategoryId: subCategoryId, skillId: null });
          selectedSkillsNames.push(subCategoryName);
        }

        sub.skills.forEach(skill => {
          if (keys[skill.skillId]?.checked) {
            selectedSkills.push({ categoryId, subcategoryId: subCategoryId, skillId: skill.skillId });
            selectedSkillsNames.push(skill.name);
          }
        });
      });
    } else {
      // Only emit category if none of its direct skills are selected
      const hasSelectedSkills = category.skills.some(skill => keys[skill.skillId]?.checked);

      if (keys[categoryId]?.checked && !hasSelectedSkills) {
        selectedSkills.push({ categoryId, subcategoryId: null, skillId: null });
        selectedSkillsNames.push(categoryName);
      }

      category.skills.forEach(skill => {
        if (keys[skill.skillId]?.checked) {
          selectedSkills.push({ categoryId, subcategoryId: null, skillId: skill.skillId });
          selectedSkillsNames.push(skill.name);
        }
      });
    }
  });

  return { selectedSkills, selectedSkillsNames };
};

// ── Component ─────────────────────────────────────────────────────

const CandidateSkillsSelector = ({
  keys,
  skills,
  index,
  form,
  automationStore,
  candidateSkillsHierarchy,
  formType,
}) => {
  // Memoize derived data from hierarchy (only recomputes if hierarchy changes)
  const treeOptions = useMemo(
    () => mapHierarchyToOptions(candidateSkillsHierarchy),
    [candidateSkillsHierarchy]
  );

  const { skillMap, subCatMap, catMap } = useMemo(
    () => buildNameMaps(candidateSkillsHierarchy),
    [candidateSkillsHierarchy]
  );

  const { ancestors, descendants } = useMemo(
    () => buildRelationMaps(candidateSkillsHierarchy),
    [candidateSkillsHierarchy]
  );

  // ── Core: apply a clean keys object to the store ──────────────

  const applyKeys = (cleanKeys) => {
    if (formType === "updaterecord") {
      const updatedForm = { ...form, keys: cleanKeys };

      if (!cleanKeys || Object.keys(cleanKeys).length === 0) {
        updatedForm.names = "";
        updatedForm.skills = [];
        automationStore.setFormUpdateRecord(updatedForm);
        return;
      }

      const { selectedSkills, selectedSkillsNames } = processKeysToSkills(candidateSkillsHierarchy, cleanKeys);
      updatedForm.skills = selectedSkills;
      updatedForm.names = selectedSkillsNames.join(", ");
      automationStore.setFormUpdateRecord(updatedForm);
    } else {
      const updatedForm = [...form];
      const currentItem = { ...updatedForm[index] };
      currentItem.thirdColumn = { ...currentItem.thirdColumn, keys: cleanKeys };

      if (!cleanKeys || Object.keys(cleanKeys).length === 0) {
        currentItem.thirdColumn.names = "";
        currentItem.thirdColumn.skills = [];
        updatedForm[index] = currentItem;
        if (formType === "ifelse") {
          automationStore.setFormIfElse(updatedForm);
        } else {
          automationStore.setFormTrigger(updatedForm);
        }
        return;
      }

      const { selectedSkills, selectedSkillsNames } = processKeysToSkills(candidateSkillsHierarchy, cleanKeys);
      currentItem.thirdColumn.skills = selectedSkills;
      currentItem.thirdColumn.names = selectedSkillsNames.join(", ");
      updatedForm[index] = currentItem;

      if (formType === "ifelse") {
        automationStore.setFormIfElse(updatedForm);
      } else {
        automationStore.setFormTrigger(updatedForm);
      }
    }
  };

  // ── Selection handlers ────────────────────────────────────────
  // Hierarchical rules:
  //   SELECT a node   → also select all its ANCESTORS (can't have child without parent)
  //   DESELECT a node → also deselect all its DESCENDANTS (children can't exist without parent)

  const handleNodeSelect = (e) => {
    const nodeKey = e.node.key;
    const updated = { ...(keys || {}) };

    // Select the clicked node
    updated[nodeKey] = { checked: true, partialChecked: false };

    // Auto-select all ancestors (parent, grandparent, etc.)
    (ancestors[nodeKey] || []).forEach(ancestorKey => {
      if (!updated[ancestorKey]?.checked) {
        updated[ancestorKey] = { checked: true, partialChecked: false };
      }
    });

    applyKeys(updated);
  };

  const handleNodeUnselect = (e) => {
    const nodeKey = e.node.key;
    const updated = { ...(keys || {}) };

    // Deselect the clicked node
    delete updated[nodeKey];

    // Auto-deselect all descendants (children, grandchildren, etc.)
    (descendants[nodeKey] || []).forEach(descKey => {
      delete updated[descKey];
    });

    applyKeys(Object.keys(updated).length === 0 ? null : updated);
  };

  // ── Chip removal (× button) ───────────────────────────────────
  // Same logic as deselect: removing a chip also removes all descendants.

  const handleRemove = (entry) => {
    const currentKeys = formType === "updaterecord"
      ? form.keys
      : form[index]?.thirdColumn?.keys;

    const updated = { ...(currentKeys || {}) };

    // Determine the node key being removed
    const nodeKey = entry.skillId || entry.subcategoryId || entry.categoryId;
    delete updated[nodeKey];

    // Also remove all descendants
    (descendants[nodeKey] || []).forEach(descKey => {
      delete updated[descKey];
    });

    applyKeys(Object.keys(updated).length === 0 ? null : updated);
  };

  // ── Chip rendering ────────────────────────────────────────────

  const getChipLabel = (entry) => {
    if (!entry.subcategoryId && !entry.skillId) {
      return catMap[entry.categoryId];
    }
    if (entry.subcategoryId && !entry.skillId) {
      return `${catMap[entry.categoryId]} - ${subCatMap[entry.subcategoryId]}`;
    }
    if (entry.subcategoryId && entry.skillId) {
      return `${catMap[entry.categoryId]} - ${subCatMap[entry.subcategoryId]} - ${skillMap[entry.skillId]}`;
    }
    return `${catMap[entry.categoryId]} - ${skillMap[entry.skillId]}`;
  };

  const renderChips = (selectedSkills) => {
    if (!selectedSkills || selectedSkills.length === 0) {
      return <span className="p-placeholder">-Select-</span>;
    }

    return (
      <div className="selected-skills" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
        {selectedSkills.map((entry, idx) => (
          <span key={idx} className="selected-skill">
            {getChipLabel(entry)}
            <button
              className="selected-skill-remove"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(entry);
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <Row className="text-left mb-3">
      <Col md={12}>
        <TreeSelect
          value={keys}
          options={treeOptions}
          onChange={() => {}}
          onNodeSelect={handleNodeSelect}
          onNodeUnselect={handleNodeUnselect}
          metaKeySelection={false}
          closeIcon={false}
          selectionMode="checkbox"
          appendTo="self"
          placeholder="-Select-"
          className="candidate-skills-hierarchy"
          valueTemplate={() => renderChips(skills)}
          disabled={automationStore.automationState !== Enum.AutomationStateType.Draft}
        />
      </Col>
    </Row>
  );
};

export default CandidateSkillsSelector;
